import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';
import logger from '@/logger';
import { CustomerProcessVariable } from '@/pages/CustomerSupport/types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

interface CustomerVariablesStore {
	variables: CustomerProcessVariable[];
	isLoading: boolean;
	error: string | null;

	loadVariables: (organizationId: string) => Promise<void>;
	insertVariable: (
		organizationId: string,
		variable: Omit<
			CustomerProcessVariable,
			'id' | 'created_at' | 'created_by' | 'organization_id' | 'effective_from'
		>
	) => Promise<boolean>;
	resetError: () => void;
}

export const useCustomerVariablesStore = create<CustomerVariablesStore>((set, get) => ({
	variables: [],
	isLoading: false,
	error: null,

	loadVariables: async (organizationId: string) => {
		try {
			set({ isLoading: true, error: null });

			console.log('Loading variables from Supabase for organization_id:', organizationId);

			const { data, error } = await supabase
				.from('finclu_customer_process_variables')
				.select('*')
				.eq('organization_id', organizationId)
				.order('effective_from', { ascending: false });

			if (error) {
				console.error('Supabase error loading variables:', error);
				logger.error('Error loading customer variables:', error);
				set({ error: error.message, isLoading: false });
				return;
			}

			console.log('Loaded variables:', data);

			const processedData = (data || []).map((variable: any) => ({
				...variable,
				variable_value:
					typeof variable.variable_value === 'string'
						? parseFloat(variable.variable_value)
						: variable.variable_value
			}));

			set({
				variables: processedData,
				isLoading: false
			});
		} catch (err) {
			console.error('Exception loading variables:', err);
			logger.error('Error loading customer variables:', err);
			set({
				error: 'Failed to load customer variables',
				isLoading: false
			});
		}
	},

	insertVariable: async (
		organizationId: string,
		variable: Omit<
			CustomerProcessVariable,
			'id' | 'created_at' | 'created_by' | 'organization_id' | 'effective_from'
		>
	) => {
		try {
			set({ error: null });

			const {
				data: { session }
			} = await supabase.auth.getSession();
			const createdBy = session?.user?.email || null;

			const { error: insertError } = await supabase
				.from('finclu_customer_process_variables')
				.insert([
					{
						organization_id: organizationId,
						variable_name: variable.variable_name,
						variable_value: variable.variable_value,
						text_value: variable.text_value,
						variable_type: variable.variable_type,
						description: variable.description,
						created_by: createdBy
					}
				]);

			if (insertError) {
				logger.error('Error inserting customer variable:', insertError);
				set({ error: insertError.message });
				return false;
			}

			await get().loadVariables(organizationId);
			return true;
		} catch (err) {
			logger.error('Error inserting customer variable:', err);
			set({ error: 'Failed to insert customer variable' });
			return false;
		}
	},

	resetError: () => set({ error: null })
}));
